import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Building2, FlaskConical, FileSpreadsheet, PlayCircle, Loader2, ArrowRight, Bot, UserCheck, AlertTriangle, CalendarClock, TrendingDown, TrendingUp, Minus } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import ScoreRing from "../../components/compliance/ScoreRing";
import Sparkline from "../../components/compliance/Sparkline";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "never");

export default function CompliancePage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => authApi.compliance.overview().then(setData).catch((err) => toast.error("Could not load compliance overview", { description: errorMessage(err) })), []);
  useEffect(() => {
    load();
    authApi.compliance.catalog().then(setCatalog).catch(() => null);
  }, [load]);
  useEffect(() => {
    if (!data?.tenants.some((t) => t.running)) return undefined;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [data, load]);

  const run = async (t) => {
    setBusyId(t.id);
    try {
      await authApi.compliance.start(t.id);
      toast.success(`CIS scan started for ${t.name}`);
      await load();
    } catch (err) {
      toast.error("Could not start the CIS scan", { description: errorMessage(err) });
    } finally {
      setBusyId("");
    }
  };

  const tenants = data?.tenants || [];
  return (
    <div className="ws-page" data-testid="compliance-page">
      <header className="ws-head">
        <div>
          <h1>
            <ClipboardCheck size={20} /> Compliance
          </h1>
          <p>CIS Microsoft 365 Foundations Benchmark v7.0.0 - Level 1 and Level 2 posture per tenant, with attestations, accepted risks and executive reports.</p>
        </div>
      </header>

      {catalog && (
        <div className="kpi-grid" data-testid="cis-framework-kpis">
          <div className="kpi glass">
            <span className="kpi-ico">
              <ClipboardCheck size={16} />
            </span>
            <div>
              <div className="kpi-value mono">{catalog.total}</div>
              <div className="kpi-label">CIS controls</div>
              <div className="kpi-sub">{catalog.sections.reduce((n, s) => n + s.l1, 0)} Level 1 · {catalog.sections.reduce((n, s) => n + s.l2, 0)} Level 2</div>
            </div>
          </div>
          <div className="kpi glass">
            <span className="kpi-ico">
              <Bot size={16} />
            </span>
            <div>
              <div className="kpi-value mono">{catalog.automated}</div>
              <div className="kpi-label">Automated via Microsoft Graph</div>
              <div className="kpi-sub">Entra, Conditional Access, PIM, devices, SharePoint, Intune, DNS</div>
            </div>
          </div>
          <div className="kpi glass">
            <span className="kpi-ico">
              <UserCheck size={16} />
            </span>
            <div>
              <div className="kpi-value mono">{catalog.total - catalog.automated}</div>
              <div className="kpi-label">Manual attestation</div>
              <div className="kpi-sub">Exchange, Purview, Teams, Fabric, Defender policies</div>
            </div>
          </div>
        </div>
      )}

      {data === null && <div className="ws-empty">Loading...</div>}
      {data && tenants.length === 0 && (
        <div className="ws-empty glass" data-testid="compliance-empty">
          <Building2 size={28} />
          <h3>No tenants linked yet</h3>
          <p>Link a Microsoft tenant first, then run a CIS scan from here.</p>
          <Link to="/app/tenants" className="tb-btn primary">
            Go to tenants <ArrowRight size={14} />
          </Link>
        </div>
      )}

      <div className="tenant-grid" data-testid="compliance-tenant-grid">
        {tenants.map((t) => {
          const csv = t.source === "csv";
          const last = t.last;
          const busy = busyId === t.id;
          return (
            <article key={t.id} className="tenant-card glass cis-tenant-card" data-testid={`cis-tenant-card-${t.id}`}>
              <div className="tenant-top">
                <Link to={`/app/compliance/${t.id}`} className="tenant-name" data-testid="cis-tenant-link">
                  {t.sample && <FlaskConical size={14} className="sample-ico" title="Sample data" />}
                  {csv && <FileSpreadsheet size={14} className="sample-ico" title="Imported from CSV" />}
                  {t.name}
                </Link>
                <span className="lic-chips">
                  {last && <span className={`mini-chip ${last.profile === "E5" ? "sec-suite" : ""}`}>{last.profile || "E3"} profile</span>}
                  {!csv && t.schedule !== "off" && t.cis_schedule !== false && (
                    <span className="mini-chip ok" title={`CIS scan runs with the ${t.schedule} schedule`} data-testid="cis-tenant-scheduled">
                      <CalendarClock size={10} /> {t.schedule}
                    </span>
                  )}
                </span>
              </div>
              <div className="cis-tenant-body">
                <ScoreRing percent={csv ? null : last?.l1 ?? null} size={84} label="L1" testId={`cis-tenant-score-${t.id}`} />
                <dl className="tenant-meta">
                  <div>
                    <dt>Level 1 + 2</dt>
                    <dd className="mono">{last?.l2 === null || last?.l2 === undefined ? "—" : `${last.l2}%`}</dd>
                  </div>
                  <div>
                    <dt>Pass / Fail</dt>
                    <dd className="mono">{last ? `${last.counts.pass} / ${last.counts.fail}` : "—"}</dd>
                  </div>
                  <div>
                    <dt>Trend (L1)</dt>
                    <dd className="cis-trend-cell">
                      {t.trend?.filter((v) => v !== null).length > 1 ? <Sparkline values={t.trend} testId={`cis-tenant-spark-${t.id}`} /> : <span className="muted">—</span>}
                      {last?.delta !== null && last?.delta !== undefined && (
                        <span className={`cis-delta-chip ${last.delta < 0 ? "crit" : last.delta > 0 ? "ok" : ""}`} data-testid={`cis-tenant-delta-${t.id}`}>
                          {last.delta < 0 ? <TrendingDown size={11} /> : last.delta > 0 ? <TrendingUp size={11} /> : <Minus size={11} />} {last.delta > 0 ? "+" : ""}
                          {last.delta}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Last CIS scan</dt>
                    <dd>{t.running ? "running..." : fmtDate(last?.finished_at)}</dd>
                  </div>
                </dl>
              </div>
              {csv && (
                <div className="ws-alert">
                  <AlertTriangle size={13} /> CSV-imported tenant - CIS checks need a Graph connection.
                </div>
              )}
              {last?.overdue > 0 && (
                <div className="ws-alert warn" data-testid="cis-tenant-overdue">
                  <CalendarClock size={13} /> {last.overdue} attestation{last.overdue === 1 ? "" : "s"} past the review date.
                </div>
              )}
              {last?.missing_permissions > 0 && (
                <button type="button" className="ws-alert warn clickable" onClick={() => navigate(`/app/compliance/${t.id}?setup=1`)} title={(last.missing_list || []).join(", ")} data-testid="cis-tenant-missing-perms">
                  <AlertTriangle size={13} />
                  <span>
                    {last.missing_permissions} Graph permission{last.missing_permissions === 1 ? "" : "s"} missing - some controls not evaluated. <b>See which →</b>
                  </span>
                </button>
              )}
              <div className="tenant-actions">
                <button type="button" className="tb-btn sm" onClick={() => navigate(`/app/compliance/${t.id}`)} data-testid="cis-tenant-open">
                  <ClipboardCheck size={13} /> Open dashboard
                </button>
                {isAdmin && !csv && (
                  <button type="button" className="tb-btn sm feature cost" disabled={busy || !!t.running} onClick={() => run(t)} data-testid="cis-tenant-run">
                    {busy || t.running ? <Loader2 size={13} className="spin" /> : <PlayCircle size={13} />} {t.running ? "Scanning..." : "Run CIS scan"}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
